/**
 * A client's API access: the switch that turns it on, and the keys its systems
 * use. Webhooks have their own card and switch (`client-webhooks-card`).
 *
 * Off by default, and off means the client has no trace of it: no page in its
 * portal, its keys refused as if wrong. Keys the client made while it was on are
 * kept, and work again when it is switched back.
 * The client makes its own keys — a key is shown once, to whoever will store it —
 * so staff here can only see and revoke them.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Code2 } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  Input,
  QueryPending,
  ReadOnly,
  Switch,
  api,
  formatRelative,
  toast,
  useCan,
} from '@connect/web-shared';

import { messagingKeys } from './types';

interface ApiKey {
  public_id: string;
  name: string;
  prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** One client API feature (`docs/43_CLIENT_API_FEATURES.md`), with this client's switch. */
interface ApiFeature {
  name: string;
  label: string;
  description: string;
  enabled: boolean;
  /** False: not part of API access — shown on its own card (connected apps). */
  needs_api_access?: boolean;
  /** Stopped for now — the client's own email content, by Connect's watch or by staff. */
  paused?: boolean;
  paused_at?: string | null;
  paused_note?: string | null;
}

/** A client's sandbox limits (null: the installation's) and the recipients it verified itself. */
interface SandboxView {
  recipient_limit: number | null;
  daily_limit: number | null;
  default_recipient_limit: number;
  default_daily_limit: number;
  recipients: { kind: string; address: string; verified: boolean; verified_at: string | null }[];
}

interface IntegrationSettings {
  enabled: boolean;
  keys: ApiKey[];
  features: ApiFeature[];
  sandbox?: SandboxView | null;
}

const KIND_LABELS: Record<string, string> = { mobile: 'Mobile', email: 'Email', device: 'App device' };

/**
 * Sandbox keys' limits for one client, which staff raise, and the recipients the client verified —
 * read only: verifying is the client's own, in its portal (owner, 2026-10-25).
 */
function SandboxLimits({
  url,
  view,
  onSaved,
}: {
  url: string;
  view: SandboxView;
  onSaved: (data: IntegrationSettings) => void;
}) {
  const [recipients, setRecipients] = useState(view.recipient_limit?.toString() ?? '');
  const [daily, setDaily] = useState(view.daily_limit?.toString() ?? '');
  useEffect(() => {
    setRecipients(view.recipient_limit?.toString() ?? '');
    setDaily(view.daily_limit?.toString() ?? '');
  }, [view.recipient_limit, view.daily_limit]);
  const save = useMutation({
    mutationFn: () =>
      api.put<IntegrationSettings>(`${url}/sandbox`, {
        recipient_limit: recipients.trim() ? Number(recipients) : null,
        daily_limit: daily.trim() ? Number(daily) : null,
      }),
    onSuccess: (data) => {
      onSaved(data);
      toast.success('Sandbox limits saved');
    },
    onError: (caught) => toast.error('Could not save them', caught instanceof ApiError ? caught.message : undefined),
  });
  const changed =
    recipients !== (view.recipient_limit?.toString() ?? '') || daily !== (view.daily_limit?.toString() ?? '');
  return (
    <div className="space-y-3 rounded-control border border-line px-3 py-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Test recipients"
          inputMode="numeric"
          value={recipients}
          placeholder={String(view.default_recipient_limit)}
          hint="Empty: the installation's."
          onChange={(event) => setRecipients(event.target.value.replace(/\D/g, ''))}
        />
        <Input
          label="Messages a day per test key"
          inputMode="numeric"
          value={daily}
          placeholder={String(view.default_daily_limit)}
          hint="Empty: the installation's."
          onChange={(event) => setDaily(event.target.value.replace(/\D/g, ''))}
        />
      </div>
      {changed ? (
        <div className="flex justify-end">
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            Save limits
          </Button>
        </div>
      ) : null}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted">Verified by the client · {view.recipients.length}</p>
        {view.recipients.length ? (
          <ul className="space-y-1 text-sm">
            {view.recipients.map((row) => (
              <li key={`${row.kind}:${row.address}`} className="flex flex-wrap items-center gap-2">
                <span className="text-muted">{KIND_LABELS[row.kind] ?? row.kind}</span>
                <span className="break-all font-mono text-xs">{row.address}</span>
                {row.verified ? <Badge tone="success">Verified</Badge> : <Badge tone="neutral">Code sent</Badge>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted">None yet. The client adds them itself, with a code to each.</p>
        )}
      </div>
    </div>
  );
}

export function IntegrationsCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.integration.edit');
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const url = `/messaging/clients/${clientPublicId}/integrations`;
  const key = messagingKeys.integrations(clientPublicId);

  const settings = useQuery({ queryKey: key, queryFn: () => api.get<IntegrationSettings>(url) });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put<IntegrationSettings>(url, { enabled }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success(data.enabled ? `API access on for ${clientName}` : `API access off for ${clientName}`);
    },
    onError: (caught) => toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined),
  });

  const feature = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      api.put<IntegrationSettings>(`${url}/features/${name}`, { enabled }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success('Saved');
    },
    onError: (caught) => toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined),
  });
  const directContent = useMutation({
    mutationFn: (action: 'pause' | 'resume') =>
      api.post<IntegrationSettings>(`${url}/direct-content/${action}`, action === 'pause' ? { note: '' } : undefined),
    onSuccess: (data, action) => {
      queryClient.setQueryData(key, data);
      toast.success(action === 'pause' ? 'Own content stopped' : 'Own content restarted');
    },
    onError: (caught) => toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined),
  });

  const data = settings.data;
  const liveKeys = data?.keys.filter((k) => !k.revoked_at) ?? [];

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Code2 className="size-4" />
              API access
            </span>
          }
          description={`Lets ${clientName}'s own systems send with API keys.`}
        />
        <CardBody className="space-y-4">
          {data === undefined ? (
            <QueryPending query={settings} label="Loading API access" />
          ) : (
            <>
              <Switch
                label="API access"
                checked={data.enabled} disabled={toggle.isPending}
                hint={
                  data.enabled
                    ? `${clientName} manages its keys under API keys in its app.`
                    : `Off: ${clientName} sees nothing of it, and any keys it made are refused.`
                }
                onChange={(event) => toggle.mutate(event.target.checked)}
              />
  
              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Client API features</h3>
                <p className="text-xs text-muted">
                  Off by default. {clientName}'s systems read what is on from its compose options and are refused
                  anything that is off. Limits for these are under Email → Limits.
                </p>
                {data.features.filter((item) => item.needs_api_access !== false).map((item) => (
                  <div key={item.name} className="space-y-2">
                    <Switch
                      label={item.label}
                      checked={item.enabled}
                      disabled={feature.isPending}
                      hint={item.description}
                      onChange={(event) => feature.mutate({ name: item.name, enabled: event.target.checked })}
                    />
                    {item.enabled && item.paused ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-line px-3 py-2 text-sm">
                        <span className="min-w-0">
                          <Badge tone="warning">Stopped</Badge>{' '}
                          <span className="text-muted">
                            {item.paused_note ?? 'Stopped'}
                            {item.paused_at ? ` · ${formatRelative(item.paused_at)}` : ''}
                          </span>
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={directContent.isPending}
                          onClick={() => directContent.mutate('resume')}
                        >
                          Restart
                        </Button>
                      </div>
                    ) : item.enabled && item.name === 'sandbox' && data.sandbox ? (
                      <SandboxLimits
                        url={url}
                        view={data.sandbox}
                        onSaved={(saved) => queryClient.setQueryData(key, saved)}
                      />
                    ) : item.enabled && item.name === 'direct_email_content' ? (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={directContent.isPending}
                          onClick={() => directContent.mutate('pause')}
                        >
                          Stop own content now
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </section>

              {data.keys.length ? (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">
                    Keys · {liveKeys.length} live
                  </h3>
                  <ul className="divide-y divide-line rounded-control border border-line">
                    {data.keys.map((row) => (
                      <li key={row.public_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className="min-w-0">
                          <span className="text-heading">{row.name}</span>{' '}
                          <span className="font-mono text-xs text-muted">{row.prefix}_…</span>
                        </span>
                        <span className="flex items-center gap-2 text-xs text-muted">
                          {row.revoked_at ? (
                            <Badge tone="neutral">Revoked</Badge>
                          ) : (
                            <>
                              {row.last_used_at ? `Used ${formatRelative(row.last_used_at)}` : 'Never used'}
                              <Can permission="messaging.integration.delete">
                                <Button variant="ghost" size="sm" onClick={() => setRevoking(row)}>
                                  Revoke
                                </Button>
                              </Can>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </>
          )}
        </CardBody>
  
        <Confirm
          open={revoking !== null}
          onClose={() => setRevoking(null)}
          title="Revoke this key?"
          message={`${clientName}'s system using "${revoking?.name ?? ''}" will be refused from now on. Only ${clientName} can make a new one.`}
          confirmLabel="Revoke key"
          onConfirm={async () => {
            if (!revoking) return;
            try {
              await api.post(`${url}/keys/${revoking.public_id}/revoke`);
              void queryClient.invalidateQueries({ queryKey: key });
            } catch (caught) {
              toast.error('Could not revoke it', caught instanceof ApiError ? caught.message : undefined);
            }
          }}
        />
      </Card>
    </ReadOnly>
  );
}
